import { CallEmsApi, iEmsApiResponseBody, networkFailure } from "../Logic/TenantAPI";

/*
 * DTOs for the mailbox settings test routes and the resolved-settings read.
 *
 * Contract: `docs/api/mail-processing-contract.md` §1 (wire format) and §3.5
 * (mailbox tests) in the backend repo. The OpenAPI document names these with
 * long CLR names; the short names here are the client's own.
 *
 * Every property the server can leave null is typed OPTIONAL, not
 * `| null` — null properties are omitted from the wire entirely (§1.2), so an
 * explicit null never arrives.
 *
 * All three routes are tenant-scoped (the token selects the tenant) and all
 * three require sys-admin. A `403` has an empty body; `CallEmsApi` does not
 * parse one, so nothing here needs to guard against it.
 */

/**
 * How far a test got. `Completed` is the only passing stage.
 *
 * Receive never reports `Send`; send never reports `Lock`. One type for both
 * anyway — the labels are shared, and a stage that "cannot" arrive costs
 * nothing to label.
 */
export type MailboxTestStage = "Resolve" | "Lock" | "Connect" | "Send" | "Completed";

/** Fields both test results carry. */
export interface MailboxTestResultBase {
    success: boolean;
    stage: MailboxTestStage;
    /** "Basic" | "Office365" | "Graph". Absent when the test failed at `Resolve`. */
    provider?: string;
    /** Absent when the test failed at `Resolve`. */
    server?: string;
    /** Absent when the test failed at `Resolve`. */
    port?: number;
    /** Human-readable summary. Present when `success` is true. */
    message?: string;
    /** The server's exception message, safe to show as-is. Present when `success` is false. */
    error?: string;
    elapsedMs: number;
}

export interface TestMailboxReceiveResult extends MailboxTestResultBase {
    /** Present only when the test passed. Absent is not zero. */
    messageCount?: number;
}

export interface TestMailboxSendResult extends MailboxTestResultBase {
    /** The address the email went from. Absent when the test failed at `Resolve`. */
    from?: string;
    recipient?: string;
}

/** The settings the processor resolved for one direction. */
export interface ResolvedMailConfiguration {
    identity?: string;
    server?: string;
    port?: number;
    provider?: string;
    username?: string;
    /** Masked by the server. Show it exactly as received. */
    password?: string;
    msalTenantId?: string;
    msalClientId?: string;
    /** Masked by the server. Show it exactly as received. */
    msalClientSecret?: string;
    /** False means no connector exists for this provider, so the processor cannot use these settings. */
    connectorRegistered: boolean;
}

/** Exactly one of `settings` and `error` is present. */
export interface ResolvedDirection {
    settings?: ResolvedMailConfiguration;
    error?: string;
}

export interface MailboxEffectiveConfiguration {
    mailboxID: number;
    name?: string;
    address?: string;
    cloudProcessing: { inbound: boolean; outbound: boolean };
    inbound: ResolvedDirection;
    outbound: ResolvedDirection;
}

/**
 * Connects to the mailbox with its SAVED settings, counts what is waiting and
 * disconnects. Fetches and deletes nothing.
 *
 * Can take several seconds: it waits for the per-mailbox processing lock and
 * returns `stage: "Lock"` if the processor holds it. A `200` can still be a
 * failed test — read `success`.
 */
export async function TestMailboxReceive(
    token: string,
    mailboxId: number,
): Promise<iEmsApiResponseBody<TestMailboxReceiveResult>> {
    try {
        return await CallEmsApi<TestMailboxReceiveResult>(
            token,
            "POST",
            `MailProcessing/Mailboxes/${mailboxId}/Test/Receive`,
        );
    } catch {
        return networkFailure<TestMailboxReceiveResult>();
    }
}

/**
 * Sends ONE REAL EMAIL from the mailbox to `recipient`, with its SAVED
 * settings. Subject: `EMS mailbox test – {mailbox name}`.
 *
 * A `400` means the recipient is missing or malformed. A `200` can still be a
 * failed test — read `success`.
 */
export async function TestMailboxSend(
    token: string,
    mailboxId: number,
    recipient: string,
): Promise<iEmsApiResponseBody<TestMailboxSendResult>> {
    try {
        return await CallEmsApi<TestMailboxSendResult>(
            token,
            "POST",
            `MailProcessing/Mailboxes/${mailboxId}/Test/Send`,
            {
                recipient,
            },
        );
    } catch {
        return networkFailure<TestMailboxSendResult>();
    }
}

/** The effective connection settings the processor uses for this mailbox. Secrets arrive masked. */
export async function GetMailboxConfiguration(
    token: string,
    mailboxId: number,
): Promise<iEmsApiResponseBody<MailboxEffectiveConfiguration>> {
    try {
        return await CallEmsApi<MailboxEffectiveConfiguration>(
            token,
            "GET",
            `MailProcessing/Mailboxes/${mailboxId}/Configuration`,
        );
    } catch {
        return networkFailure<MailboxEffectiveConfiguration>();
    }
}
